import { forwardRef, HTMLAttributes, useMemo } from 'react'

import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'

import { mergeProps } from '@/src/utils'

import { useGetSourceIcon } from '@/src/hooks/useGetSourceIcon'
import { useTrackMutedIndicator } from '@/src/hooks/room/track/useTrackMutedIndicator'
import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface TrackMutedIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  trackRef: TrackReferenceOrPlaceholder
  show?: 'always' | 'muted' | 'unmuted'
}

/**
 * The `TrackMutedIndicator` shows whether the participant's camera or microphone is muted or not.
 * By default, a muted/unmuted icon is displayed for a camera, microphone, and screen sharing track.
 *
 * @example
 * ```tsx
 * <TrackMutedIndicator source={Track.Source.Camera} />
 * <TrackMutedIndicator source={Track.Source.Microphone} />
 * ```
 * @public
 */
export const TrackMutedIndicator = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  TrackMutedIndicatorProps
>(function TrackMutedIndicator(
  { trackRef, show = 'always', ...props }: TrackMutedIndicatorProps,
  ref,
) {
  const { className, isMuted } = useTrackMutedIndicator(trackRef)
  const { text_primary_color } = useAppSelector(selectBranding)

  const showIndicator =
    show === 'always' || (show === 'muted' && isMuted) || (show === 'unmuted' && !isMuted)

  const htmlProps = useMemo(
    () =>
      mergeProps(props, {
        className,
        style: {
          ...props?.style,
          fill: text_primary_color ?? '#fff',
        },
      }),
    [props, className, text_primary_color],
  )

  if (!showIndicator) {
    return null
  }

  const Icon = useGetSourceIcon(trackRef.source, !isMuted)

  return (
    <div ref={ref} {...htmlProps} data-lk-muted={isMuted}>
      {props.children ?? Icon}
    </div>
  )
})
